import { supabase } from "@/integrations/supabase/client";
import { LinkedInWizardData } from "@/pages/LinkedInWizard";

export interface LinkedInProfileData {
  id?: string;
  user_id?: string;
  profile_name?: string | null;
  description?: string | null;
  use_case?: string | null;
  user_categories: string[];
  primary_goals: string[];
  unique_selling_points: string[];
  custom_usp?: string | null;
  target_audience?: string | null;
  what_you_help?: string | null;
  how_you_help?: string | null;
  value_proposition?: string | null;
  industries: string[];
  audience_seniority?: string | null;
  company_size?: string | null;
  uploaded_files?: any;
  pasted_text?: string | null;
  manually_entered_text?: string | null;
  skip_experience_highlights?: boolean | null;
  experience_highlights?: any;
  skip_results_and_proof?: boolean | null;
  results_and_proof?: string | null;
  generated_headlines?: any;
  headline_recommendation?: any;
  voice_style?: string | null;
  include_faith?: string | null;
  keywords?: string | null;
  target_job_titles?: string | null;
  past_job_titles?: string | null;
  skills?: string | null;
  call_to_action?: string | null;
  status?: 'draft' | 'ready_for_review' | 'approved' | 'generated' | 'content_saved';
  generated_profile?: any;
  raw_content_data?: any;
  generated_report_data?: any;
  content_sources?: any;
  last_used_at?: string;
  created_at?: string;
  updated_at?: string;
}

export class LinkedInProfileService {
  static async saveProfile(data: LinkedInWizardData, profileId?: string, profileName?: string): Promise<LinkedInProfileData | null> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('User not authenticated');
      }

      // Transform the wizard data to match database schema
      // Build profile data WITHOUT status - status should be preserved on updates
      const profileData = {
        user_id: session.user.id,
        linkedin_url: data.linkedinUrl ?? null,
        photo_url: data.photoUrl ?? null,
        profile_name: profileName || (data.targetJobTitles ? `${data.targetJobTitles} Profile` : 'Untitled Profile'),
        user_categories: data.userCategories || [],
        primary_goals: data.primaryGoals || [],
        unique_selling_points: data.uniqueSellingPoints || [],
        custom_usp: data.customUSP,
        target_audience: data.targetAudience,
        what_you_help: data.whatYouHelp,
        how_you_help: data.howYouHelp,
        value_proposition: data.valueProposition,
        industries: data.industries || [],
        audience_seniority: data.audienceSeniority,
        company_size: data.companySize,
        uploaded_files: data.uploadedFiles?.map(file => ({
          fileName: file.file.name,
          fileSize: file.file.size,
          fileType: file.file.type,
          extractedText: file.extractedText,
          processingStatus: file.processingStatus,
          processingProgress: file.processingProgress
        })) || [],
        pasted_text: data.pastedText,
        manually_entered_text: data.manuallyEnteredText,
        skip_experience_highlights: data.skipExperienceHighlights,
        experience_highlights: data.experienceHighlights || [],
        skip_results_and_proof: data.skipResultsAndProof,
        results_and_proof: data.resultsAndProof,
        generated_headlines: data.generatedHeadlines || [],
        headline_recommendation: data.headlineRecommendation,
        voice_style: data.voiceStyle,
        include_faith: data.includeFaith,
        keywords: data.keywords,
        target_job_titles: data.targetJobTitles,
        past_job_titles: data.pastJobTitles,
        skills: data.skills,
        call_to_action: data.callToAction
        // NOTE: status is intentionally omitted - it should be preserved on updates
      };

      let result;
      if (profileId) {
        // Update existing profile
        const { data: updatedProfile, error } = await supabase
          .from('linkedin_profiles')
          .update(profileData)
          .eq('id', profileId)
          .eq('user_id', session.user.id)
          .select()
          .single();

        if (error) throw error;
        result = updatedProfile;
      } else {
        // Create new profile - only set 'draft' status for new profiles
        const { data: newProfile, error } = await supabase
          .from('linkedin_profiles')
          .insert({ ...profileData, status: 'draft' as const })
          .select()
          .single();

        if (error) throw error;
        result = newProfile;
      }

      return result;
    } catch (error) {
      console.error('Error saving LinkedIn profile:', error);
      throw error;
    }
  }

  static async saveProfileWithRawContent(data: LinkedInWizardData, rawContentData: any, profileId?: string): Promise<LinkedInProfileData | null> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('User not authenticated');
      }

      const profileData = {
        user_id: session.user.id,
        profile_name: rawContentData.profileName || 'Untitled Profile',
        use_case: rawContentData.useCase || 'LinkedIn Profile Optimization',
        description: rawContentData.description || 'LinkedIn profile optimization with collected content and form responses',
        user_categories: data.userCategories || [],
        primary_goals: data.primaryGoals || [],
        unique_selling_points: data.uniqueSellingPoints || [],
        custom_usp: data.customUSP,
        target_audience: data.targetAudience,
        what_you_help: data.whatYouHelp,
        how_you_help: data.howYouHelp,
        value_proposition: data.valueProposition,
        industries: data.industries || [],
        audience_seniority: data.audienceSeniority,
        company_size: data.companySize,
        uploaded_files: data.uploadedFiles?.map(file => ({
          fileName: file.file.name,
          fileSize: file.file.size,
          fileType: file.file.type,
          extractedText: file.extractedText,
          processingStatus: file.processingStatus,
          processingProgress: file.processingProgress
        })) || [],
        pasted_text: data.pastedText,
        manually_entered_text: data.manuallyEnteredText,
        skip_experience_highlights: data.skipExperienceHighlights,
        experience_highlights: data.experienceHighlights || [],
        skip_results_and_proof: data.skipResultsAndProof,
        results_and_proof: data.resultsAndProof,
        generated_headlines: data.generatedHeadlines || [],
        headline_recommendation: data.headlineRecommendation,
        voice_style: data.voiceStyle,
        include_faith: data.includeFaith,
        keywords: data.keywords,
        target_job_titles: data.targetJobTitles,
        past_job_titles: data.pastJobTitles,
        skills: data.skills,
        call_to_action: data.callToAction,
        raw_content_data: rawContentData,
        content_sources: rawContentData.uploadedFiles || [],
        status: 'content_saved' as const
      };

      let result;
      if (profileId) {
        // Update existing profile
        const { data: updatedProfile, error } = await supabase
          .from('linkedin_profiles')
          .update(profileData)
          .eq('id', profileId)
          .eq('user_id', session.user.id)
          .select()
          .single();

        if (error) throw error;
        result = updatedProfile;
      } else {
        // Create new profile
        const { data: newProfile, error } = await supabase
          .from('linkedin_profiles')
          .insert(profileData)
          .select()
          .single();

        if (error) throw error;
        result = newProfile;
      }

      return result;
    } catch (error) {
      console.error('Error saving LinkedIn profile with raw content:', error);
      throw error;
    }
  }

  static async saveGeneratedReport(profileId: string, generatedProfile: any, reportData: any, profileName?: string): Promise<LinkedInProfileData | null> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('User not authenticated');
      }

      // Build update object - include profile_name if provided and current name is generic
      const updateData: any = {
        generated_profile: generatedProfile,
        generated_report_data: reportData,
        status: 'generated',
        updated_at: new Date().toISOString()
      };

      // Only update profile_name if a meaningful one is provided
      if (profileName && profileName !== 'Untitled Profile') {
        updateData.profile_name = profileName;
      }

      const { data: updatedProfile, error } = await supabase
        .from('linkedin_profiles')
        .update(updateData)
        .eq('id', profileId)
        .eq('user_id', session.user.id)
        .select()
        .single();

      if (error) throw error;
      return updatedProfile as LinkedInProfileData;
    } catch (error) {
      console.error('Error saving generated report:', error);
      throw error;
    }
  }

  static async loadProfile(profileId: string): Promise<LinkedInProfileData | null> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('User not authenticated');
      }

      const { data: profile, error } = await supabase
        .from('linkedin_profiles')
        .select('*')
        .eq('id', profileId)
        .eq('user_id', session.user.id)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw error;
      }

      return profile as LinkedInProfileData;
    } catch (error) {
      console.error('Error loading LinkedIn profile:', error);
      return null;
    }
  }

  static async getAllProfiles(userId?: string): Promise<LinkedInProfileData[]> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('User not authenticated');
      }

      const targetUserId = userId || session.user.id;

      const { data: profiles, error } = await supabase
        .from('linkedin_profiles')
        .select('*')
        .eq('user_id', targetUserId)
        .order('updated_at', { ascending: false });

      if (error) throw error;

      return profiles as LinkedInProfileData[];
    } catch (error) {
      console.error('Error loading LinkedIn profiles:', error);
      return [];
    }
  }

  static async duplicateProfile(profileId: string, newName: string): Promise<LinkedInProfileData | null> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('User not authenticated');
      }

      // Get the original profile
      const originalProfile = await this.loadProfile(profileId);
      if (!originalProfile) {
        throw new Error('Profile not found');
      }

      // Create a copy with new name and reset status
      const duplicateData = {
        user_id: originalProfile.user_id,
        profile_name: newName,
        description: originalProfile.description,
        use_case: originalProfile.use_case,
        user_categories: originalProfile.user_categories,
        primary_goals: originalProfile.primary_goals,
        unique_selling_points: originalProfile.unique_selling_points,
        custom_usp: originalProfile.custom_usp,
        target_audience: originalProfile.target_audience,
        what_you_help: originalProfile.what_you_help,
        how_you_help: originalProfile.how_you_help,
        value_proposition: originalProfile.value_proposition,
        industries: originalProfile.industries,
        audience_seniority: originalProfile.audience_seniority,
        company_size: originalProfile.company_size,
        uploaded_files: originalProfile.uploaded_files,
        pasted_text: originalProfile.pasted_text,
        manually_entered_text: originalProfile.manually_entered_text,
        skip_experience_highlights: originalProfile.skip_experience_highlights,
        experience_highlights: originalProfile.experience_highlights,
        skip_results_and_proof: originalProfile.skip_results_and_proof,
        results_and_proof: originalProfile.results_and_proof,
        generated_headlines: originalProfile.generated_headlines,
        headline_recommendation: originalProfile.headline_recommendation,
        voice_style: originalProfile.voice_style,
        include_faith: originalProfile.include_faith,
        keywords: originalProfile.keywords,
        target_job_titles: originalProfile.target_job_titles,
        past_job_titles: originalProfile.past_job_titles,
        skills: originalProfile.skills,
        call_to_action: originalProfile.call_to_action,
        status: 'draft' as const
      };

      const { data: newProfile, error } = await supabase
        .from('linkedin_profiles')
        .insert(duplicateData)
        .select()
        .single();

      if (error) throw error;
      return newProfile as LinkedInProfileData;
    } catch (error) {
      console.error('Error duplicating LinkedIn profile:', error);
      throw error;
    }
  }

  static async updateProfileStatus(profileId: string, status: 'draft' | 'ready_for_review' | 'approved' | 'generated'): Promise<void> {
    try {
      const { error } = await supabase
        .from('linkedin_profiles')
        .update({ status })
        .eq('id', profileId);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating profile status:', error);
      throw error;
    }
  }

  static async deleteProfile(profileId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('linkedin_profiles')
        .delete()
        .eq('id', profileId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting LinkedIn profile:', error);
      throw error;
    }
  }

  static transformToWizardData(profileData: LinkedInProfileData): LinkedInWizardData {
    return {
      userCategories: profileData.user_categories || [],
      primaryGoals: profileData.primary_goals || [],
      uniqueSellingPoints: profileData.unique_selling_points || [],
      customUSP: profileData.custom_usp,
      targetAudience: profileData.target_audience || '',
      whatYouHelp: profileData.what_you_help || '',
      howYouHelp: profileData.how_you_help || '',
      valueProposition: profileData.value_proposition || '',
      industries: profileData.industries || [],
      audienceSeniority: profileData.audience_seniority || '',
      companySize: profileData.company_size || '',
      uploadedFiles: [], // Files will be handled separately
      pastedText: profileData.pasted_text,
      manuallyEnteredText: profileData.manually_entered_text,
      skipExperienceHighlights: profileData.skip_experience_highlights,
      experienceHighlights: profileData.experience_highlights || [],
      skipResultsAndProof: profileData.skip_results_and_proof,
      resultsAndProof: profileData.results_and_proof || '',
      generatedHeadlines: profileData.generated_headlines,
      headlineRecommendation: profileData.headline_recommendation,
      voiceStyle: profileData.voice_style || '',
      includeFaith: profileData.include_faith || '',
      keywords: profileData.keywords || '',
      targetJobTitles: profileData.target_job_titles || '',
      pastJobTitles: profileData.past_job_titles || '',
      skills: profileData.skills || '',
      callToAction: profileData.call_to_action || ''
    };
  }
}